/**
 * useExport - Hook para lógica de exportação
 *
 * Responsável por:
 * - Gerenciar estado da exportação
 * - Orquestrar preparação e execução
 * - Tracking de progresso em tempo real
 * - Tratamento de erros e cancelamento
 */

import { useState, useCallback, useRef, useEffect } from "react";
import { storage } from "../../../lib/storage/storage";
import { Keys } from "../../../lib/storage/keys";
import {
	sendToTerminal,
	debugLog,
	terminalLog,
} from "../../../lib/terminalLogger";
import { useMonitoramentoObjetos } from "../../OrderDetails/hooks/useMonitoramentoObjetos";
import { usePermissions } from "../../../contexts/PermissionContext";
import { useNotifications } from "../../feedback/NotificationProvider";
import { ErrorCode } from "../../../lib/errors";
import { ErrorCatalog } from "../../feedback/ErrorCatalog";
import { api } from "../../../lib/api";
import type {
	ExportFormat,
	ExportMode,
	ExportType,
	ExportState,
	ExportResult,
	ProcessedFile,
	CurrentSaveData,
} from "../types";

// Importar serviços reutilizáveis
import { resolveExportDirectory } from "../services/directoryResolver";
import {
	resolveFilePath,
	type ExportPayload,
} from "../../../services/pathResolverService";
import { requestConfirmation } from "../../../lib/globalConfirm";
import { syncTargetsFromCorel } from "../services/targetSyncService";
import {
	prepareExportContext,
	buildExportRequest,
} from "../services/exportContextBuilder";
import { buildBatchPayload, postBatchExport } from "../services/batchApi";
import {
	buildLayoutPngPayload,
	calculateLayoutScale,
} from "../services/layoutPayloadBuilder";
import { buildLayoutApiPayload, postLayoutExport } from "../services/layoutApi";
import { LAYOUT_EXPORT_CONFIG } from "../constants/layoutConfig";
import { PRODUCTION_SECTORS } from "../../../constants/sectors";
import { buildIdempotencyKey } from "../utils/idempotency";
import { getRecommendation } from "../utils/recommendations";
import { createExportLogger } from "../utils/exportLogger";
import { fetchExportRules } from "../services/exportRulesService";
import { corelCommunication } from "../../../lib/corel/communication";
import { abortManager } from "../../../lib/corel/pipeline/abortManager";
import { getActionTimeout } from "../../../lib/corel/timeoutService";
import { COREL_PROGRESS_SILENCE_TIMEOUT_MS } from "../../../config";
import type { ExportProfile } from "../services/exportRulesService";

/**
 * Função auxiliar para retry com backoff
 * @param fn Função assíncrona a ser executada
 * @param retries Número de tentativas (padrão: 3)
 * @param delay Delay entre tentativas em ms (padrão: 200)
 * @param context Nome da operação para logs
 */
async function retryWithBackoff<T>(
	fn: () => Promise<T>,
	retries = 3,
	delay = 200,
	context = "operation",
): Promise<T> {
	for (let i = 0; i < retries; i++) {
		try {
			const result = await fn();
			return result;
		} catch (error) {
			if (i === retries - 1) {
				sendToTerminal(
					`❌ [RETRY:FAILED] ${context} failed after ${retries} attempts`,
					{
						error: error instanceof Error ? error.message : String(error),
					},
				);
				throw error;
			}
			await new Promise((resolve) => setTimeout(resolve, delay));
		}
	}
	throw new Error(`Retry failed for ${context}`);
}

/**
 * Atualizar estágio de produção
 * @param orcId ID do orçamento
 * @param oitId ID do item
 * @param ostId ID do status do orçamento
 * @param userId ID do usuário logado
 * @param currentSector Setor atual
 * @param nextSectorProduction Próximo setor de produção
 * @param nextSectorOrcamento Próximo setor do orçamento
 * @param token Token de autenticação
 */
async function updateProductionStage(
	orcId: number,
	oitId: number,
	ostId: number,
	userId: number,
	currentSector: number,
	nextSectorProduction: number,
	nextSectorOrcamento: number,
): Promise<any> {
	const payload = {
		orc_id: orcId,
		oit_id: oitId,
		ost_id: ostId,
		user_usucadastro: userId,
		sector: currentSector,
		nextSectorProduction: {
			sectorProduction: nextSectorProduction,
			sectorOrcamento: nextSectorOrcamento,
		},
		stop_orc: false,
		source: "CorelDraw", // ✅ Campo obrigatório
	};

	// api() já adiciona o token automaticamente via TokenService
	const response = await api(`/update-production-stage/${oitId}`, {
		method: "PUT",
		body: JSON.stringify(payload),
	});

	// Validar resposta do backend
	if (response?.success === false || response?.ok === false) {
		const errorMsg =
			response?.message ||
			response?.msg ||
			"Erro desconhecido ao atualizar estágio";
		throw new Error(errorMsg);
	}

	return response;
}

interface UseExportParams {
	savedData: CurrentSaveData | null;
	onComplete: (result: ExportResult) => void;
}

export function useExport({ savedData, onComplete }: UseExportParams) {
	// Hooks de contexto
	const { userInfo } = usePermissions();
	const { notify } = useNotifications();

	// Guard de ambiente para logs de debug
	const isDevelopment = import.meta.env.MODE !== "production";

	// Helper para logar de forma segura (evita catches aninhados)
	const safeLog = useCallback(
		(message: string, data?: any) => {
			if (!isDevelopment) return; // Logs apenas em desenvolvimento
			try {
				sendToTerminal(message, data);
			} catch (err) {
				// Erro ao logar não deve quebrar o fluxo
				debugLog(`[EXPORT] Erro ao logar: ${message}`, { error: err });
			}
		},
		[isDevelopment],
	);

	// Validação defensiva: garantir que savedData existe
	const [initError, setInitError] = useState<string | null>(null);

	useEffect(() => {
		if (!savedData) {
			setInitError(
				"Dados de exportação não disponíveis. Por favor, retorne e tente novamente.",
			);
		} else {
			setInitError(null);
		}
	}, [savedData]);

	const [state, setState] = useState<ExportState>({
		format: "tiff",
		mode: "batch",
		exportType: "both", // Default: exportar ambos (produção + layout)
		recommendedFormat: null,
		status: "idle",
		progress: null,
		result: null,
		error: null,
		cancelRequested: false,
		productionError: null,
		layoutError: null,
	});

	// UI avançada / overrides de formato
	const [showAdvanced, setShowAdvanced] = useState(false);
	const [useOverrides, setUseOverrides] = useState(false);
	const [overrides, setOverrides] = useState<Record<string, unknown>>({});
	const [profiles, setProfiles] = useState<ExportProfile[] | null>(null);
	const [activeProfile, setActiveProfile] = useState<ExportProfile | null>(
		null,
	);
	const [loadingRules, setLoadingRules] = useState(false);
	const [rulesError, setRulesError] = useState<string | null>(null);

	// Persistência simples de overrides por formato (storage unificado)
	const overridesStoreKey = Keys.export.overrides;
	const loadOverridesForFormat = useCallback((fmt: ExportFormat) => {
		try {
			const all =
				storage.get<Record<string, Record<string, unknown>>>(
					overridesStoreKey,
					{
						area: "session",
						fallback: {},
					},
				) || {};
			return all[fmt] || {};
		} catch {
			return {};
		}
	}, []);
	const saveOverridesForFormat = useCallback(
		(fmt: ExportFormat, value: Record<string, unknown>) => {
			try {
				const all =
					storage.get<Record<string, Record<string, unknown>>>(
						overridesStoreKey,
						{
							area: "session",
							fallback: {},
						},
					) || {};
				all[fmt] = value || {};
				storage.set(overridesStoreKey, all, { area: "session" });
			} catch { }
		},
		[],
	);

	const cancelRequestedRef = useRef(false);
	const progressPrevRef = useRef(0);
	const processedMapRef = useRef<Record<string, ProcessedFile>>({});
	const targetsRef = useRef<any[]>([]);
	const lastIndexConfirmedRef = useRef(0);
	// ✅ MVP FIX: Ref para armazenar requestId da exportação atual (para cleanup)
	const currentExportRequestIdRef = useRef<string | null>(null);

	// ✅ Desabilitar autoInit: quando chegamos na tela de exportação, já temos todos os dados
	// O monitor só será usado se realmente precisar fazer sync (quando savedData.syncMeta?.synced for false)
	const monitoradorObjetos = useMonitoramentoObjetos({
		autoInit: false,
	});
	const loggerRef = useRef<ReturnType<typeof createExportLogger> | null>(null);

	// Controlar se já buscou perfis nesta montagem
	const fetchedRulesRef = useRef(false);

	// Carregar perfis/regras (com tratamento de erro e retry automático para timeout)
	const retryCountRef = useRef(0);
	const MAX_RETRIES = 2;

	const fetchRules = useCallback(async (isRetry = false) => {
		setLoadingRules(true);
		setRulesError(null);
		try {
			const res = await fetchExportRules();
			const list = res?.profiles || [];
			setProfiles(list);
			setActiveProfile(list[0] || null);
			retryCountRef.current = 0; // Reset ao ter sucesso
			fetchedRulesRef.current = true; // Marcar como buscado

			// Log se não houver perfis disponíveis (usar terminalLog para debug)
		} catch (e: any) {
			const status = e?.status || e?.data?.status;
			const message = e instanceof Error ? e.message : String(e);

			// Retry automático para timeout (408)
			if (status === 408 && retryCountRef.current < MAX_RETRIES) {
				retryCountRef.current++;
				const delay = 1000 * retryCountRef.current; // 1s, 2s
				setLoadingRules(false);
				await new Promise((r) => setTimeout(r, delay));
				return fetchRules(true); // Recursive retry
			}

			// Mensagens de erro específicas
			const specific =
				status === 401
					? "Não autorizado. Faça login novamente."
					: status === 403
						? "Acesso negado às regras de exportação."
						: status === 404
							? "Regras de exportação não encontradas."
							: status === 408
								? `Tempo esgotado ao carregar regras (${retryCountRef.current}/${MAX_RETRIES} tentativas).`
								: `Falha ao carregar regras (${message})`;
			setRulesError(specific);
		} finally {
			setLoadingRules(false);
		}
	}, []);

	// 🔄 SEMPRE buscar profiles quando abrir a página (dados mudam constantemente)
	useEffect(() => {
		if (state.status === "idle" && !loadingRules && !fetchedRulesRef.current) {
			fetchRules();
		}
	}, [state.status, loadingRules]); // ← Removido fetchRules das dependências

	// Reset do flag quando componente desmontar
	useEffect(() => {
		return () => {
			fetchedRulesRef.current = false;
		};
	}, []);

	// Determinar formato recomendado ao montar
	useEffect(() => {
		if (!savedData) return;

		const proId = savedData.produto?.pro_id || savedData.produto?.id;
		const productName = savedData.produto?.nome;

		const recommendation = getRecommendation({
			proId: proId || null,
			productName: productName || null,
		});

		if (recommendation) {
			setState((prev) => ({
				...prev,
				format: recommendation.format,
				recommendedFormat: recommendation.format,
			}));

			// Formato recomendado (debug apenas - removido log)
		}
	}, [savedData]);

	// Handler de progresso
	const handleProgress = useCallback((progressData: any) => {
		try {
			loggerRef.current?.attach("progress.event", progressData);
		} catch { }
		const statusRaw = String(progressData?.status ?? "");

		// Percentual
		let targetPct: number | null = null;
		if (typeof progressData?.percentComplete !== "undefined") {
			const pctNum = Number(progressData.percentComplete);
			if (!Number.isNaN(pctNum)) targetPct = Math.max(0, Math.min(100, pctNum));
		}

		// Índice e total
		const index = Number(progressData?.index ?? progressData?.current ?? 0);
		const total = Number(progressData?.total ?? 0);
		const name = (progressData?.name ?? progressData?.fileName) as
			| string
			| null;

		// Atualizar processedFiles
		const staticId = String(progressData?.staticId ?? "");
		const status = statusRaw;
		const outputPath = String(progressData?.outputPath ?? "");
		const isFileSuccess =
			status === "file_success" || status === "ok" || status === "success";
		const isFileError = status === "file_error" || status === "error";

		if (staticId && (isFileSuccess || isFileError)) {
			processedMapRef.current[staticId] = {
				name: name || staticId,
				status: isFileError ? "error" : "success",
				path: outputPath,
			};
		}

		// Inferir percentual do índice/total se não veio
		if (targetPct == null && total > 0) {
			targetPct = (index / total) * 100;
		}

		setState((prev) => ({
			...prev,
			progress: {
				percent: targetPct ?? prev.progress?.percent ?? 0,
				current: index,
				total,
				currentFile: name,
				processedFiles: Object.values(processedMapRef.current),
			},
		}));
	}, []);

	// Executar exportação com tracking de progresso usando corelCommunication
	const executeExportWithProgress = useCallback(
		async (request: any): Promise<any> => {
			// ✅ LOG: Verificar se função está sendo chamada
			console.log("🔍 [EXPORT] executeExportWithProgress chamado", {
				action: request?.action,
				requestId: request?.requestId,
			});
			sendToTerminal("🔍 [EXPORT] executeExportWithProgress chamado", {
				action: request?.action,
				requestId: request?.requestId,
			});

			return new Promise((resolve, reject) => {
				let resolved = false;
				let maxTimeoutId: NodeJS.Timeout | null = null; // Hard limit (painel admin)
				let silenceTimeoutId: NodeJS.Timeout | null = null; // Reseta a cada progress

				// Helper para limpar ambos os timeouts
				const clearAllTimeouts = () => {
					if (maxTimeoutId) clearTimeout(maxTimeoutId);
					if (silenceTimeoutId) clearTimeout(silenceTimeoutId);
				};

				// Helper para iniciar/resetar o silence timeout
				const resetSilenceTimeout = () => {
					if (silenceTimeoutId) clearTimeout(silenceTimeoutId);
					const silenceMinutes = Math.round(
						COREL_PROGRESS_SILENCE_TIMEOUT_MS / 60000,
					);
					silenceTimeoutId = setTimeout(() => {
						if (!resolved) {
							resolved = true;
							clearAllTimeouts();
							sendToTerminal("⏱️ [EXPORT:SILENCE_TIMEOUT]", {
								silenceMs: COREL_PROGRESS_SILENCE_TIMEOUT_MS,
								silenceMinutes,
							});
							reject(
								new Error(
									`Timeout: sem resposta do C# por ${silenceMinutes} minutos`,
								),
							);
						}
					}, COREL_PROGRESS_SILENCE_TIMEOUT_MS);
				};

				try {
					loggerRef.current?.attach("webview.request", request);
				} catch { }

				// Callback que processa respostas do CorelDRAW
				const responseHandler = (response: any) => {
					if (resolved) return;

					try {
						try {
							loggerRef.current?.attach("webview.message", response);
						} catch { }

						const status = response?.status;

						// Log mensagem recebida
						try {
							terminalLog.debug("[EXPORT:MESSAGE_MATCHED]", {
								requestId: request.requestId,
								action: response?.action,
								status,
								hasResult: !!response?.result,
							});
						} catch { }

						// Progresso - não finalizar a Promise ainda, MAS resetar silence timeout
						if (status === "progress" || status === "progress_update") {
							handleProgress(response);
							// ✅ Resetar silence timeout a cada progress recebido
							resetSilenceTimeout();
							return; // Manter handler ativo
						}

						// Resultado final - Aceitar múltiplos status
						if (
							status === "ok" ||
							status === "error" ||
							status === "erro" ||
							status === "cancelled"
						) {
							resolved = true;
							clearAllTimeouts();

							if (status === "ok") {
								try {
									loggerRef.current?.log(
										"INFO",
										"export.result",
										"ok",
										response,
									);
									sendToTerminal("✅ [EXPORT:RESULT_OK]", {
										summary: response?.result?.summary,
										itemsCount: response?.result?.items?.length,
									});
								} catch { }
								resolve(response);
							} else if (status === "cancelled") {
								try {
									loggerRef.current?.log(
										"WARN",
										"export.result",
										"cancelled",
										response,
									);
									sendToTerminal("⚠️ [EXPORT:RESULT_CANCELLED]", {
										message: response?.message,
									});
								} catch { }
								reject(new Error("Exportação cancelada pelo usuário"));
							} else {
								// status === 'erro' ou 'error'
								const errorMsg =
									response?.message || response?.error || "Erro na exportação";
								const errorCode = response?.code;
								try {
									loggerRef.current?.log(
										"ERROR",
										"export.result",
										"error",
										response,
									);
									sendToTerminal("❌ [EXPORT:RESULT_ERROR]", {
										message: errorMsg,
										code: errorCode,
									});
								} catch { }
								reject(new Error(errorMsg));
							}
						}
					} catch (err) {
						try {
							terminalLog.debug("[EXPORT:MESSAGE_PARSE_ERROR]", {
								error: err instanceof Error ? err.message : String(err),
							});
						} catch { }
					}
				};

				// ✅ LOG DETALHADO: Payload completo enviado para backend C#
				const payloadToSend = {
					action: request.action,
					requestId: request.requestId,
					data: request.data,
				};

				// Log simples primeiro (garantir que aparece)
				console.log(
					"📤 [EXPORT:PAYLOAD_TO_CORELLDRAW] Payload completo enviado para backend C#",
				);
				sendToTerminal(
					"📤 [EXPORT:PAYLOAD_TO_CORELLDRAW] Payload completo enviado para backend C#",
				);

				// Log detalhado
				console.log("📤 [EXPORT:PAYLOAD_DETAILS]", {
					action: request.action,
					requestId: request.requestId,
					format: request.data?.format,
					overridesCount: request.data?.overrides
						? Object.keys(request.data.overrides).length
						: 0,
					targetsCount: request.data?.targets ? request.data.targets.length : 0,
				});
				sendToTerminal("📤 [EXPORT:PAYLOAD_DETAILS]", {
					action: request.action,
					requestId: request.requestId,
					payloadSize: JSON.stringify(payloadToSend).length,
					profile_id: request.data?.profile_id,
					format: request.data?.format,
					output_dir: request.data?.output_dir,
					snapshotId: request.data?.snapshotId,
					snapshotPolicy: request.data?.snapshotPolicy,
					bbox: request.data?.bbox,
					idempotencyKey: request.data?.idempotencyKey,
					hasSmb: !!request.data?.smb,
					overridesCount: request.data?.overrides
						? Object.keys(request.data.overrides).length
						: 0,
					targetsCount: request.data?.targets ? request.data.targets.length : 0,
					filename_template: request.data?.filename_template,
				});

				// Log completo do payload (pode ser grande)
				console.log("📤 [EXPORT:PAYLOAD_COMPLETE]", payloadToSend);
				sendToTerminal("📤 [EXPORT:PAYLOAD_COMPLETE]", {
					payload: payloadToSend,
					overrides: request.data?.overrides, // ✅ TODAS as opções de exportação
					targets: request.data?.targets, // ✅ Lista completa de targets
				});

				// Log JSON formatado para fácil leitura
				console.log(
					"📤 [EXPORT:PAYLOAD_JSON]",
					JSON.stringify(payloadToSend, null, 2),
				);
				try {
					terminalLog.debug("[EXPORT:PAYLOAD_JSON]", {
						payload: JSON.stringify(payloadToSend, null, 2),
					});
				} catch { }

				// Buscar timeout do painel admin (fallback: 1h) - hard limit máximo
				const exportTimeout = getActionTimeout(
					request.action || "corel-export-batch",
				);
				const maxTimeoutMinutes = Math.round(exportTimeout / 60000);

				// Enviar usando corelCommunication
				const requestId = corelCommunication.sendActionGuarded(
					request.action,
					request.data,
					responseHandler,
					exportTimeout, // timeout do painel admin
				);

				if (!requestId) {
					reject(new Error("Falha ao enviar comando de exportação"));
					return;
				}

				// ✅ Timeout máximo absoluto (hard limit do painel admin) - NUNCA reseta
				maxTimeoutId = setTimeout(() => {
					if (!resolved) {
						resolved = true;
						clearAllTimeouts();
						sendToTerminal("⏱️ [EXPORT:MAX_TIMEOUT]", {
							maxTimeoutMs: exportTimeout,
							maxTimeoutMinutes,
						});
						reject(
							new Error(
								`Timeout: operação excedeu limite máximo de ${maxTimeoutMinutes} minutos`,
							),
						);
					}
				}, exportTimeout);

				// ✅ Silence timeout (reseta a cada progress) - inicia imediatamente
				resetSilenceTimeout();
			});
		},
		[handleProgress],
	);

	// Executar exportação de LAYOUT (PNG) com tracking de progresso
	const executeLayoutExport = useCallback(
		async (
			savedData: CurrentSaveData,
			targets: any[],
			requestId: string,
			idempotencyKey: string,
		): Promise<any> => {
			sendToTerminal("🟢 [LAYOUT:EXPORT_START]", {
				targetsCount: targets.length,
				config: LAYOUT_EXPORT_CONFIG,
			});

			try {
				// 1. Build payload
				const payload = buildLayoutPngPayload(savedData, targets, requestId);

				// Payload construído - silencioso

				// 2. Execute via webview
				setState((prev) => ({ ...prev, status: "exporting-layout" }));

				const layoutResult = await new Promise((resolve, reject) => {
					let resolved = false;
					let maxTimeoutId: NodeJS.Timeout | null = null; // Hard limit (painel admin)
					let silenceTimeoutId: NodeJS.Timeout | null = null; // Reseta a cada progress

					// Helper para limpar ambos os timeouts
					const clearAllTimeouts = () => {
						if (maxTimeoutId) clearTimeout(maxTimeoutId);
						if (silenceTimeoutId) clearTimeout(silenceTimeoutId);
					};

					// Helper para iniciar/resetar o silence timeout
					const resetSilenceTimeout = () => {
						if (silenceTimeoutId) clearTimeout(silenceTimeoutId);
						const silenceMinutes = Math.round(
							COREL_PROGRESS_SILENCE_TIMEOUT_MS / 60000,
						);
						silenceTimeoutId = setTimeout(() => {
							if (!resolved) {
								resolved = true;
								clearAllTimeouts();
								sendToTerminal("⏱️ [LAYOUT:SILENCE_TIMEOUT]", {
									silenceMs: COREL_PROGRESS_SILENCE_TIMEOUT_MS,
									silenceMinutes,
								});
								reject(
									new Error(
										`Timeout: sem resposta do C# por ${silenceMinutes} minutos`,
									),
								);
							}
						}, COREL_PROGRESS_SILENCE_TIMEOUT_MS);
					};

					// Callback que processa respostas do CorelDRAW
					const responseHandler = (response: any) => {
						if (resolved) return;

						try {
							const status = response?.status;
							const type = response?.type;

							// Log mensagem recebida
							try {
								terminalLog.debug("[EXPORT:LAYOUT_MESSAGE_MATCHED]", {
									requestId,
									status,
									type,
									hasResult: !!response?.result,
								});
							} catch { }

							// Progresso - não finalizar a Promise ainda, MAS resetar silence timeout
							if (status === "progress" || status === "progress_update") {
								// ✅ Resetar silence timeout a cada progress recebido
								resetSilenceTimeout();
								return; // Manter handler ativo
							}

							// Aceitar múltiplos formatos de sucesso
							if (
								type === "export-complete" ||
								type === "layout-complete" ||
								status === "ok"
							) {
								resolved = true;
								clearAllTimeouts();

								// Suportar ambos os formatos: exportados[] (PNG) e items[] (Batch)
								const itemsCount =
									response?.result?.exportados?.length ||
									response?.result?.items?.length ||
									response?.items?.length ||
									0;
								const falhasCount = response?.result?.falhas?.length || 0;
								const total =
									response?.result?.total || itemsCount + falhasCount;

								sendToTerminal("✅ [LAYOUT:RESULT_OK]", {
									itemsCount,
									falhasCount,
									total,
									hasPartialSuccess: falhasCount > 0,
								});
								resolve(response);
							}
							// Aceitar múltiplos formatos de erro
							else if (
								type === "export-error" ||
								type === "layout-error" ||
								status === "error" ||
								status === "erro" ||
								status === "cancelled"
							) {
								resolved = true;
								clearAllTimeouts();
								const errorMsg =
									response?.error ||
									response?.message ||
									"Erro na exportação de layout";
								sendToTerminal("❌ [LAYOUT:RESULT_ERROR]", {
									message: errorMsg,
									code: response?.code,
								});
								reject(new Error(errorMsg));
							}
						} catch (err) {
							try {
								terminalLog.debug("[EXPORT:LAYOUT_MESSAGE_PARSE_ERROR]", {
									error: err instanceof Error ? err.message : String(err),
								});
							} catch { }
						}
					};

					// Enviar usando corelCommunication
					// ✅ LOG DETALHADO: Payload completo de layout enviado para backend C#
					try {
						sendToTerminal(
							"📤 [LAYOUT:PAYLOAD_TO_CORELLDRAW] Payload completo de layout enviado para backend C#",
							{
								action: payload.action,
								requestId: payload.requestId,
								payloadSize: JSON.stringify(payload).length,
								payload: payload,
								// Detalhamento do data
								dataDetails: {
									pastaDestino: payload.data?.pastaDestino,
									redimensionarPorEscala: payload.data?.redimensionarPorEscala,
									transparencia: payload.data?.transparencia,
									resolucaoDpi: payload.data?.resolucaoDpi,
									nomeLayout: payload.data?.nomeLayout,
									itemsCount: payload.data?.items
										? payload.data.items.length
										: 0,
									items: payload.data?.items, // ✅ Lista completa de items do layout
								},
							},
						);

						// Log adicional formatado para fácil leitura
						terminalLog.debug("[LAYOUT:PAYLOAD_JSON]", {
							payload: JSON.stringify(payload, null, 2),
						});
					} catch (logError) {
						// Não quebrar o fluxo se o log falhar
						try {
							sendToTerminal("⚠️ [LAYOUT] Erro ao logar payload", {
								error: String(logError),
							});
						} catch { }
					}

					// Buscar timeout do painel admin (fallback: 1h) - hard limit máximo
					const layoutTimeout = getActionTimeout(
						payload.action || "exportar-objetos-png-lite",
					);
					const maxTimeoutMinutes = Math.round(layoutTimeout / 60000);

					const sentRequestId = corelCommunication.sendActionGuarded(
						payload.action,
						payload.data,
						responseHandler,
						layoutTimeout, // timeout do painel admin
					);

					if (!sentRequestId) {
						reject(
							new Error("Falha ao enviar comando de exportação de layout"),
						);
						return;
					}

					// ✅ Timeout máximo absoluto (hard limit do painel admin) - NUNCA reseta
					maxTimeoutId = setTimeout(() => {
						if (!resolved) {
							resolved = true;
							clearAllTimeouts();
							sendToTerminal("⏱️ [LAYOUT:MAX_TIMEOUT]", {
								maxTimeoutMs: layoutTimeout,
								maxTimeoutMinutes,
							});
							reject(
								new Error(
									`Timeout: operação excedeu limite máximo de ${maxTimeoutMinutes} minutos`,
								),
							);
						}
					}, layoutTimeout);

					// ✅ Silence timeout (reseta a cada progress) - inicia imediatamente
					resetSilenceTimeout();
				});

				sendToTerminal("✅ [LAYOUT:EXPORT_COMPLETE]", {
					success: true,
					files:
						layoutResult?.result?.exportados?.length ||
						layoutResult?.result?.items?.length ||
						layoutResult?.items?.length ||
						0,
					falhas: layoutResult?.result?.falhas?.length || 0,
				});

				return layoutResult;
			} catch (error) {
				safeLog("❌ [LAYOUT:EXPORT_FAILED]", {
					error: error instanceof Error ? error.message : String(error),
				});
				throw error;
			}
		},
		[],
	);

	// Lock para evitar múltiplas exportações simultâneas (proteção contra race conditions)
	const exportLockRef = useRef(false);

	// Handler principal de exportação
	const handleExport = useCallback(async () => {
		// Verificação atômica: se já está exportando, retornar imediatamente
		if (exportLockRef.current) {
			safeLog(
				"⚠️ [EXPORT] Exportação já em andamento, ignorando chamada duplicada",
			);
			return;
		}

		// Verificar novamente após possível race condition
		if (exportLockRef.current) {
			safeLog("⚠️ [EXPORT] Race condition detectada, ignorando");
			return;
		}

		exportLockRef.current = true;

		try {
			if (!savedData) {
				setState((prev) => ({
					...prev,
					status: "error",
					error: "Dados de exportação não encontrados",
				}));
				exportLockRef.current = false;
				return;
			}
			loggerRef.current = createExportLogger();
			loggerRef.current.log("INFO", "export.start", undefined, {
				orcId: savedData.pedido?.orc_id,
				oitId: savedData.pedido?.oit_id,
				proId: savedData.produto?.pro_id ?? savedData.produto?.id,
				format: state.format,
				mode: state.mode,
			});
			// Reset
			cancelRequestedRef.current = false;
			progressPrevRef.current = 0;
			processedMapRef.current = {};
			targetsRef.current = [];
			lastIndexConfirmedRef.current = 0;

			setState((prev) => ({ ...prev, status: "preparing", error: null }));

			safeLog("🚀 [EXPORT:START]", {
				format: state.format,
				mode: state.mode,
				exportType: state.exportType,
				orcId: savedData.pedido.orc_id,
				oitId: savedData.pedido.oit_id,
				hasSyncMeta: !!savedData.syncMeta,
				synced: savedData.syncMeta?.synced,
				syncSource: savedData.syncMeta?.source,
			});

			// 1. Validações iniciais - estrutura completa de objetos
			if (!savedData.objetos) {
				terminalLog.error("[EXPORT] savedData.objetos é null ou undefined");
				throw new Error("Dados de objetos não encontrados");
			}

			if (!Array.isArray(savedData.objetos)) {
				terminalLog.error("[EXPORT] savedData.objetos não é um array", {
					objetosType: typeof savedData.objetos,
				});
				throw new Error("Formato inválido: objetos deve ser um array");
			}

			if (savedData.objetos.length === 0) {
				safeLog("❌ [EXPORT] Validação falhou", { reason: "no-objects" });
				throw new Error("Nenhum objeto disponível para exportação");
			}

			// Validação: verificar estrutura de cada objeto
			const objetosInvalidos: number[] = [];
			savedData.objetos.forEach((obj, idx) => {
				if (!obj || typeof obj !== "object") {
					objetosInvalidos.push(idx);
				} else if (!obj.guid && !obj.staticId && !obj.id) {
					objetosInvalidos.push(idx);
				}
			});

			if (objetosInvalidos.length > 0) {
				terminalLog.error("[EXPORT] Objetos inválidos encontrados", {
					indicesInvalidos: objetosInvalidos,
					totalObjetos: savedData.objetos.length,
				});
				throw new Error(
					`Objetos inválidos nos índices: ${objetosInvalidos.join(", ")}`,
				);
			}

			// 2. Buscar regras de salvamento (se necessário)
			let regrasSalvamento: any[] = [];
			try {
				// Regras de salvamento podem ser buscadas aqui se necessário
				// Por enquanto, usar vazio
			} catch (err) {
				terminalLog.warn(
					"[EXPORT] Erro ao buscar regras de salvamento (continuando)",
					{
						error: err instanceof Error ? err.message : String(err),
					},
				);
			}

			// 3. Usar perfil ativo carregado (se houver)
			const activeProfileLocal: any = activeProfile;

			// 4. Resolver diretório base — backend PathResolver com fallback local
			let directory: string;
			let usedFallback = false;

			try {
				const exportPayload: ExportPayload = {
					flow: "export",
					orc_id: Number(savedData.pedido.orc_id),
					oit_id: Number(savedData.pedido.oit_id),
					exportType: state.exportType === "layout" ? "png" : "production",
				};
				const resolved = await resolveFilePath(exportPayload);
				directory = resolved.metadata.baseDir;
				sendToTerminal("✅ [EXPORT] BaseDir resolvido via backend", {
					baseDir: directory,
					storageMethod: resolved.metadata.storageMethod,
				});
			} catch (backendError) {
				const errorMsg =
					backendError instanceof Error
						? backendError.message
						: String(backendError);
				sendToTerminal("⚠️ [EXPORT] Backend indisponível após retries", {
					error: errorMsg,
				});

				const useFallback = await requestConfirmation({
					title: "Backend indisponível",
					message: `Não foi possível resolver o diretório de exportação via servidor.\n\nDeseja usar o cálculo local como alternativa?\n\n(Erro: ${errorMsg})`,
					confirmLabel: "Usar cálculo local",
					cancelLabel: "Cancelar exportação",
				});

				if (!useFallback) {
					throw new Error("Exportação cancelada — backend indisponível.");
				}

				sendToTerminal("🔄 [EXPORT] Usuário aceitou fallback local");
				const localResult = await resolveExportDirectory({
					saved: savedData,
					regrasSalvamento,
					activeProfile: activeProfileLocal,
					format: state.format,
					useFormatOverrides: false,
					manualFormat: null,
				});
				directory = localResult.directory;
				usedFallback = localResult.usedFallback;
			}

			// 5. Sincronizar targets (OU usar pre-sincronizados)
			let targets: any[];
			let source: string;
			let usedStaleData = false;

			if (savedData.syncMeta?.synced) {
				targets = savedData.objetos || [];
				source = "pre-synced";
				loggerRef.current?.log("INFO", "targets.pre-synced", undefined, {
					count: targets.length,
					source: savedData.syncMeta.source,
				});
			} else {
				const syncResult = await syncTargetsFromCorel({
					saved: savedData,
					targetsOverride: null,
					monitoradorObjetos,
				});

				targets = syncResult.targets;
				source = syncResult.source;
				usedStaleData = syncResult.usedStaleData;
			}

			if (!targets.length) {
				try {
					loggerRef.current?.log("ERROR", "targets.sync", "empty");
				} catch { }
				sendToTerminal("❌ [EXPORT] Nenhum alvo para exportação", {});
				throw new Error("Nenhum alvo para exportação");
			}

			// 6. Gerar IDs e chaves (compartilhados entre produção e layout)
			const requestId = `export_${Date.now()}_${Math.random()
				.toString(36)
				.slice(2, 9)}`;
			try {
				loggerRef.current?.setRequestId(requestId);
			} catch { }

			// ✅ MVP FIX: Registrar operação de exportação no abortManager
			// Isso permite ignorar eventos de "documento fechado" durante exportação
			currentExportRequestIdRef.current = requestId;
			abortManager.registerOperation(requestId);

			// IDs básicos para idempotencyKey (usados mesmo para layout)
			const orcId = savedData.pedido?.orc_id || "0";
			const oitId = savedData.pedido?.oit_id || "0";
			const baseIdempotencyKey = buildIdempotencyKey(
				state.mode,
				directory,
				orcId,
				oitId,
				targets,
			);

			// ==========================================
			// FLUXO 1: PRODUÇÃO (se selecionado)
			// ==========================================
			let batchResult: any = null;
			let result: ExportResult | null = null;
			let summary: any = null;
			let postBatchSuccess = false;
			let context: any = null;

			if (state.exportType === "production" || state.exportType === "both") {
				sendToTerminal("🟢 [DUAL_EXPORT:PRODUCTION_START]", {
					exportType: state.exportType,
					targetsCount: targets.length,
				});

				// 7. Preparar contexto de exportação (apenas para produção)
				const { context: productionContext, overridesBuildResult } =
					await prepareExportContext({
						baseDir: directory,
						saved: savedData,
						targets,
						effectiveFormat: state.format,
						activeProfile,
						payloadOverrides: useOverrides ? overrides : {},
						exportMode: state.mode,
					});

				if (!overridesBuildResult.ok) {
					try {
						loggerRef.current?.log(
							"ERROR",
							"context.overrides",
							overridesBuildResult.error,
						);
					} catch { }
					sendToTerminal("❌ [EXPORT] Erro ao preparar configurações", {
						error: overridesBuildResult.error,
					});
					throw new Error(
						overridesBuildResult.error || "Erro ao preparar configurações",
					);
				}

				context = productionContext;

				// 8. Snapshot para consistência (SOMENTE se não pré-sincronizado)
				let snapshotId: string | undefined;

				if (!savedData.syncMeta?.synced) {
					try {
						const snap = await monitoradorObjetos.snapshot();
						snapshotId = snap?.snapshotId;
					} catch (error) {
						const errorMsg =
							error instanceof Error ? error.message : String(error);
						sendToTerminal("❌ [EXPORT] Snapshot falhou", { error: errorMsg });
						// Continuar sem snapshotId (não é crítico)
					}
				}

				// 9. Construir request de produção
				const request = buildExportRequest(
					state.mode,
					context,
					requestId,
					snapshotId,
					baseIdempotencyKey,
				);

				// Persistir targets para mapeamento durante progresso
				targetsRef.current = context.targetsWithNames;

				// 10. Executar exportação
				setState((prev) => ({ ...prev, status: "exporting" }));

				loggerRef.current?.log("INFO", "export.execute", undefined, {
					requestId,
					mode: state.mode,
				});

				batchResult = await executeExportWithProgress(request);

				if (batchResult?.status === "ok") {
					sendToTerminal("✅ [EXPORT] Finalizado", {
						summary: batchResult?.result?.summary,
					});
					loggerRef.current?.log("INFO", "export.complete", undefined, {
						status: batchResult?.status,
						summary: batchResult?.result?.summary,
					});
				} else {
					sendToTerminal("❌ [EXPORT] Erro na exportação", {
						status: batchResult?.status,
						message: batchResult?.message,
					});
				}

				// 11. Registrar no backend COM RETRY
				const payload = buildBatchPayload(
					savedData,
					batchResult.result || batchResult,
					state.format,
					context!.outputDir,
					targets,
				);

				if (payload) {
					try {
						await retryWithBackoff(
							async () => {
								const apiResult = await postBatchExport(
									payload,
									"",
									baseIdempotencyKey,
								);
								sendToTerminal("✅ [EXPORT:REGISTERED]", {
									success: apiResult.success,
									batchId: apiResult.data?.result?.batch_id,
								});
								loggerRef.current?.log("INFO", "api.postBatch", undefined, {
									success: apiResult.success,
									batchId: apiResult.data?.result?.batch_id,
								});
								return apiResult;
							},
							3,
							200,
							"POST /cdr/arquivos/exportar-batch",
						);
						postBatchSuccess = true;
					} catch (apiError) {
						sendToTerminal("❌ [EXPORT:REGISTER_FAILED_PERMANENTLY]", {
							error:
								apiError instanceof Error ? apiError.message : String(apiError),
						});
						loggerRef.current?.log(
							"ERROR",
							"api.postBatch.failed",
							apiError instanceof Error ? apiError.message : String(apiError),
						);

						const catalogEntry = ErrorCatalog[ErrorCode.EXPORT_REGISTER_FAILED];
						notify({
							type: "error",
							code: ErrorCode.EXPORT_REGISTER_FAILED,
							title: catalogEntry.title,
							message: catalogEntry.message,
							sticky: true,
						});

						// NÃO continuar se POST falhar
						throw new Error("Falha ao registrar exportação após 3 tentativas");
					}
				}

				// 12. Atualizar estágio de produção COM RETRY
				if (postBatchSuccess) {
					try {
						// ✅ VALIDAÇÃO: userId é obrigatório (SEM FALLBACK)
						const userId = userInfo?.id;
						if (!userId || typeof userId !== "number") {
							const errorMsg = "userId obrigatório não disponível";
							sendToTerminal(
								"❌ [UPDATE_STAGE] userId obrigatório não disponível",
								{
									userInfoExists: !!userInfo,
									orcId: savedData.pedido.orc_id,
									oitId: savedData.pedido.oit_id,
								},
							);

							// ✅ Notificar usuário via toast
							const catalogEntry =
								ErrorCatalog[ErrorCode.EXPORT_MISSING_USER_ID];
							notify({
								type: "error",
								code: ErrorCode.EXPORT_MISSING_USER_ID,
								title: catalogEntry.title,
								message: catalogEntry.message as string,
								sticky: true,
							});

							throw new Error(errorMsg);
						}

						// ✅ VALIDAÇÃO: ost_id é obrigatório (SEM FALLBACK)
						// ✅ Buscar ost_id em múltiplas fontes (compatibilidade com estruturas antigas)
						const ostId =
							savedData?.pedido?.ost_id ||
							savedData?.pedido?.status_orcamento?.id ||
							(savedData?.pedido as any)?.orc_status_id ||
							savedData?.pedido?.status?.ost_id ||
							(savedData?.pedido as any)?.status?.id;

						if (!ostId || typeof ostId !== "number") {
							const errorMsg =
								"ost_id obrigatório não encontrado em savedData.pedido";
							sendToTerminal(
								"❌ [UPDATE_STAGE] ost_id obrigatório não encontrado",
								{
									orcId: savedData.pedido.orc_id,
									oitId: savedData.pedido.oit_id,
									savedDataPedidoKeys: savedData?.pedido
										? Object.keys(savedData.pedido)
										: [],
									savedDataPedidoOstId: savedData?.pedido?.ost_id,
									savedDataPedidoStatusOrcamentoId: (savedData?.pedido as any)
										?.status_orcamento?.id,
									savedDataPedidoOrcStatusId: (savedData?.pedido as any)
										?.orc_status_id,
									savedDataPedidoStatusId: (savedData?.pedido as any)?.status
										?.id,
									savedDataKeys: savedData ? Object.keys(savedData) : [],
								},
							);

							// ✅ Notificar usuário via toast
							const catalogEntry =
								ErrorCatalog[ErrorCode.EXPORT_MISSING_OST_ID];
							notify({
								type: "error",
								code: ErrorCode.EXPORT_MISSING_OST_ID,
								title: catalogEntry.title,
								message: catalogEntry.message as string,
								sticky: true,
							});

							throw new Error(errorMsg);
						}

						const currentSector = PRODUCTION_SECTORS.ARTE_FINAL;
						const nextSectorProduction = PRODUCTION_SECTORS.PRODUCAO;
						const nextSectorOrcamento = PRODUCTION_SECTORS.PRODUCAO_ORCAMENTO;

						// Verificar status atual do item para não regredir
						// sector 3 = Em Produção; se já está em 3 ou além, não movimentar
						const currentItemStatus =
							(savedData.pedido as any)?.oit_status_id ?? 0;

						if (currentItemStatus >= currentSector) {
							// Item já está em Em Produção (3) ou além — não regredir
							sendToTerminal(
								"ℹ️ [UPDATE_STAGE] Item já está no stage " +
								currentItemStatus +
								", pulando movimentação ao exportar",
								{
									orcId: savedData.pedido.orc_id,
									oitId: savedData.pedido.oit_id,
									currentItemStatus,
									targetSector: currentSector,
								},
							);
						} else {
							await retryWithBackoff(
								async () => {
									return await updateProductionStage(
										savedData.pedido.orc_id,
										savedData.pedido.oit_id,
										ostId,
										userId,
										currentSector,
										nextSectorProduction,
										nextSectorOrcamento,
									);
								},
								3,
								200,
								"PUT /update-production-stage",
							);

							// Notificar sucesso da movimentação
							notify({
								type: "success",
								title: "Pedido Movimentado",
								message: `Pedido #${savedData.pedido.orc_id}-${savedData.pedido.oit_id} enviado para Produção`,
								sticky: false,
							});
						}

						// Aguardar 1000ms para normalização
						await new Promise((resolve) => setTimeout(resolve, 1000));
					} catch (stageError) {
						const errorMessage =
							stageError instanceof Error
								? stageError.message
								: String(stageError);

						sendToTerminal("❌ [UPDATE_STAGE:FAILED_PERMANENTLY]", {
							error: errorMessage,
						});
						loggerRef.current?.log(
							"ERROR",
							"api.updateStage.failed",
							errorMessage,
						);

						const catalogEntry =
							ErrorCatalog[ErrorCode.EXPORT_STAGE_UPDATE_FAILED];
						notify({
							type: "warning", // ✅ NÃO-CRÍTICO: mudado de 'error' para 'warning'
							code: ErrorCode.EXPORT_STAGE_UPDATE_FAILED,
							title: catalogEntry.title,
							// Incluir detalhes quando disponível
							message: errorMessage
								? `${catalogEntry.message} Detalhes: ${errorMessage}`
								: catalogEntry.message,
							sticky: false, // ✅ NÃO-CRÍTICO: mudado de true para false
						});

						// ✅ NÃO BLOQUEAR O FLUXO: A exportação foi bem-sucedida, apenas a movimentação falhou
						// O usuário pode movimentar manualmente depois se necessário
						// Removido: throw new Error('Falha ao atualizar estágio de produção após 3 tentativas');
					}
				}

				// 13. Preparar resultado da produção (MAS NÃO FINALIZAR AINDA)
				const rawSummary =
					batchResult.result?.summary || batchResult.summary || {};
				summary = {
					total: Number(rawSummary.total ?? targets.length),
					success: Number(rawSummary.success ?? targets.length),
					failed: Number(rawSummary.failed ?? 0),
					cancelled: Boolean(rawSummary.cancelled),
					duration_ms: Number(
						rawSummary.durationMs ?? batchResult?.metrics?.durationMsTotal ?? 0,
					),
				};

				result = {
					success: true,
					format: state.format,
					mode: state.mode,
					files: {
						batch: (batchResult.result?.items || batchResult.items || [])
							.map((it: any) => it.outputPath || it.caminho_exportado)
							.filter(Boolean),
					},
					summary,
					outputDir: context.outputDir,
				};

				// NÃO setState({ status: 'complete' }) AQUI!
				// Apenas logs intermediários
				try {
					loggerRef.current?.log("INFO", "production.complete", undefined, {
						summary,
					});
					sendToTerminal("✅ [PRODUCTION:COMPLETE]", {
						files: result.files.batch.length,
					});
				} catch { }
			} else {
				// Se for apenas layout, criar resultado vazio para produção
				summary = {
					total: 0,
					success: 0,
					failed: 0,
					cancelled: false,
					duration_ms: 0,
				};

				result = {
					success: true,
					format: state.format,
					mode: state.mode,
					files: {
						batch: [],
					},
					summary,
					outputDir: directory, // Usar directory base ao invés de context.outputDir
				};

				sendToTerminal("⏭️ [PRODUCTION:SKIPPED]", {
					reason: "exportType is layout only",
					exportType: state.exportType,
				});
			}

			// Limpar overrides após exportação bem-sucedida para garantir que próxima exportação use GET novamente
			if (useOverrides) {
				try {
					storage.remove(overridesStoreKey, "session");
					sendToTerminal(
						"[EXPORT] Overrides limpos após exportação bem-sucedida",
					);
				} catch { }
			}

			// ==========================================
			// FLUXO 2: LAYOUT PNG (se selecionado)
			// ==========================================
			let layoutResult: any = null;
			let layoutOutputDir = "";

			if (state.exportType === "layout" || state.exportType === "both") {
				sendToTerminal("🟢 [DUAL_EXPORT:LAYOUT_START]", {
					exportType: state.exportType,
					targetsCount: targets.length,
				});

				try {
					setState((prev) => ({ ...prev, status: "exporting-layout" }));

					// Executar exportação de layout
					const layoutRequestId = `${requestId}-layout`;
					const layoutIdempotencyKey = `${baseIdempotencyKey}-layout`;

					layoutResult = await executeLayoutExport(
						savedData,
						targets,
						layoutRequestId,
						layoutIdempotencyKey,
					);

					layoutResult.success = true;
					// Suportar ambos os formatos: exportados[] (PNG) e items[] (Batch)
					layoutResult.files =
						layoutResult?.result?.exportados?.length ||
						layoutResult?.result?.items?.length ||
						layoutResult?.items?.length ||
						0;
					layoutResult.falhas = layoutResult?.result?.falhas?.length || 0;

					// Construir diretório de layout usando a estrutura CLIENTES
					try {
						const cliId =
							savedData?.cliente?.cli_id || savedData?.cliente?.id || "0";
						// Usa nome_fantasia como preferencial, com fallback para nome
						const cliNome = (
							savedData?.cliente?.nome_fantasia ||
							savedData?.cliente?.nome ||
							"Cliente"
						)
							.replace(/[<>:"/\\|?*]/g, "_")
							.trim();
						const now = new Date();
						const ano = now.getFullYear();
						const mesNomes = [
							"JANEIRO",
							"FEVEREIRO",
							"MARÇO",
							"ABRIL",
							"MAIO",
							"JUNHO",
							"JULHO",
							"AGOSTO",
							"SETEMBRO",
							"OUTUBRO",
							"NOVEMBRO",
							"DEZEMBRO",
						];
						const mes = `${String(now.getMonth() + 1).padStart(2, "0")}-${mesNomes[now.getMonth()]
							}`;
						const dia = String(now.getDate()).padStart(2, "0");
						const mesNum = String(now.getMonth() + 1).padStart(2, "0");
						const diaFormatado = `${dia}-${mesNum}`;
						const materialRaw =
							savedData?.material?.nome ||
							savedData?.produto?.nome ||
							"Material";
						const material = materialRaw
							.toUpperCase()
							.replace(/[<>:"/\\|?*]/g, "_")
							.trim();
						const baseDir = savedData?.regras?.diretorio_base_exportpng || "";

						layoutOutputDir = `${baseDir}\\CLIENTES\\${cliId}_${cliNome}\\${ano}\\${mes}\\${diaFormatado}\\${material}\\${savedData.pedido.orc_id}\\${savedData.pedido.oit_id}\\layout`;
					} catch (e) {
						layoutOutputDir = "LAYOUT";
					}

					sendToTerminal("✅ [DUAL_EXPORT:LAYOUT_COMPLETE]", {
						files: layoutResult.files,
						outputDir: layoutOutputDir,
					});

					// POST #2: Registrar layout (SE SUCESSO) COM RETRY
					if (layoutResult.success && layoutResult.files > 0) {
						// Calcular escala usada (mesma lógica do payload)
						const alturaMaximaCm = savedData?.regras?.altura_maxima_cm ?? null;
						const escalaUsada = calculateLayoutScale(alturaMaximaCm);

						// Enviando para produção após layout
						const layoutPayload = buildLayoutApiPayload(
							savedData,
							layoutResult,
							layoutOutputDir,
							escalaUsada,
							LAYOUT_EXPORT_CONFIG.resolucaoDpi,
						);

						if (layoutPayload) {
							try {
								await retryWithBackoff(
									async () => {
										const layoutApiResult = await postLayoutExport(
											layoutPayload,
											layoutIdempotencyKey,
										);
										sendToTerminal("✅ [DUAL_EXPORT:POST_LAYOUT_SUCCESS]", {
											success: layoutApiResult.success,
										});
										layoutResult.registered = true;
										layoutResult.uploaded =
											layoutResult?.result?.exportados?.filter(
												(i: any) => i.uploaded || i.enviado_webview,
											).length || 0;
										return layoutApiResult;
									},
									3,
									200,
									"POST /arquivos/exportarPNG",
								);

								// Fechar documento após exportação de layout (não bloqueia se falhar)
								try {
									// Fechando documento - silencioso

									const closeResponse = await new Promise<any>(
										(resolve, reject) => {
											const timeoutId = setTimeout(() => {
												resolve({ status: "timeout" });
											}, 5000);

											corelCommunication.sendActionGuarded(
												"fechar-documento",
												{},
												(response) => {
													clearTimeout(timeoutId);
													resolve(response);
												},
											);
										},
									);

									// Status sempre é "ok", verificar result.sucesso
									if (
										!closeResponse?.result?.sucesso &&
										closeResponse?.status !== "timeout"
									) {
										sendToTerminal("⚠️ [DOCUMENT] Erro ao fechar documento", {
											message:
												closeResponse?.result?.mensagem ||
												"Nenhum documento ativo",
										});
									}
								} catch (closeError) {
									// Não bloquear o fluxo se falhar ao fechar
									sendToTerminal("⚠️ [DOCUMENT] Erro ao fechar documento", {
										error:
											closeError instanceof Error
												? closeError.message
												: String(closeError),
									});
								}
							} catch (layoutApiError) {
								sendToTerminal(
									"⚠️ [DUAL_EXPORT:POST_LAYOUT_FAILED_PERMANENTLY]",
									{
										error:
											layoutApiError instanceof Error
												? layoutApiError.message
												: String(layoutApiError),
									},
								);

								const catalogEntry =
									ErrorCatalog[ErrorCode.EXPORT_LAYOUT_REGISTER_FAILED];
								notify({
									type: "warning",
									code: ErrorCode.EXPORT_LAYOUT_REGISTER_FAILED,
									title: catalogEntry.title,
									message: catalogEntry.message,
									sticky: false,
								});

								layoutResult.registered = false;
								layoutResult.uploaded = 0;
							}
						}
					}
				} catch (layoutError) {
					const layoutErrorMsg =
						layoutError instanceof Error
							? layoutError.message
							: String(layoutError);
					sendToTerminal("❌ [DUAL_EXPORT:LAYOUT_FAILED]", {
						error: layoutErrorMsg,
					});
					setState((prev) => ({ ...prev, layoutError: layoutErrorMsg }));

					// Layout falhou, mas se produção teve sucesso = sucesso parcial
					layoutResult = {
						success: false,
						error: layoutErrorMsg,
						files: 0,
						registered: false,
						uploaded: 0,
					};
				}
			}

			// ==========================================
			// CONSOLIDAR RESULTADO FINAL
			// ==========================================
			const finalResult: ExportResult = {
				...result,
				exportType: state.exportType,
				partialSuccess: result.success && layoutResult && !layoutResult.success,

				// Resultado da produção
				production: {
					success: result.success,
					files: summary.success,
					error: null,
					registered:
						state.exportType === "production" || state.exportType === "both"
							? postBatchSuccess
							: false, // POST só foi feito se produção foi executada
					outputDir: context?.outputDir || result.outputDir,
				},

				// Resultado do layout (se executado)
				layout: layoutResult
					? {
						success: layoutResult.success,
						files: layoutResult.files,
						error: layoutResult.error || null,
						registered: layoutResult.registered || false,
						uploaded: layoutResult.uploaded || 0,
						outputDir: layoutOutputDir,
					}
					: undefined,

				message:
					layoutResult && !layoutResult.success
						? "Produção exportada com sucesso, mas layout falhou"
						: "Exportação completa",
			};

			// Atualizar estado para complete
			setState((prev) => ({
				...prev,
				status: "complete",
				result: finalResult,
				progress: null, // Limpar loading
			}));

			sendToTerminal("✅ [DUAL_EXPORT:COMPLETE]", {
				productionFiles: finalResult.production?.files || 0,
				layoutFiles: finalResult.layout?.files || 0,
				partialSuccess: finalResult.partialSuccess || false,
				exportType: state.exportType,
			});

			// Notificar usuário sobre conclusão
			const isPartialSuccess = finalResult.partialSuccess;
			notify({
				type: isPartialSuccess ? "warning" : "success",
				title: isPartialSuccess ? "Exportação Parcial" : "Exportação Completa",
				message: isPartialSuccess
					? `✅ Produção: ${finalResult.production?.files || 0
					} arquivo(s)\n⚠️ Layout: Falhou na exportação`
					: state.exportType === "both"
						? `✅ Produção: ${finalResult.production?.files || 0
						} arquivo(s)\n✅ Layout: ${finalResult.layout?.files || 0} PNG(s)`
						: state.exportType === "production"
							? `✅ Produção: ${finalResult.production?.files || 0
							} arquivo(s) exportados`
							: `✅ Layout: ${finalResult.layout?.files || 0} PNG(s) exportados`,
				sticky: false,
			});

			// Chamar callback para mostrar modal (delay reduzido)
			setTimeout(() => {
				onComplete(finalResult);
			}, 500);

			// ✅ MVP FIX: Desregistrar operação de exportação ao finalizar com sucesso
			try {
				abortManager.unregisterOperation(requestId);
				currentExportRequestIdRef.current = null;
			} catch { }

			// Liberar lock ao finalizar com sucesso
			exportLockRef.current = false;
		} catch (error) {
			const errorMessage =
				error instanceof Error ? error.message : String(error);

			safeLog("[EXPORT:ERROR]", { error: errorMessage });

			setState((prev) => ({
				...prev,
				status: "error",
				error: errorMessage,
				progress: null,
			}));
			try {
				loggerRef.current?.finalize("error", { error: errorMessage });
				// Removido: download de arquivo .log físico
			} catch { }

			// ✅ MVP FIX: Desregistrar operação de exportação em caso de erro
			if (currentExportRequestIdRef.current) {
				try {
					abortManager.unregisterOperation(currentExportRequestIdRef.current);
					currentExportRequestIdRef.current = null;
				} catch { }
			}

			// Liberar lock em caso de erro
			exportLockRef.current = false;
		}
	}, [
		savedData,
		state.format,
		state.mode,
		state.exportType,
		monitoradorObjetos,
		executeExportWithProgress,
		executeLayoutExport,
		onComplete,
		useOverrides,
		overrides,
		activeProfile,
		safeLog,
	]);

	// Handler de cancelamento
	const handleCancel = useCallback(() => {
		cancelRequestedRef.current = true;

		safeLog("[EXPORT:CANCEL_REQUESTED]");

		// TODO: notificar CorelDRAW sobre cancelamento se houver suporte
		// Por enquanto, apenas setamos a flag
		setState((prev) => ({ ...prev, cancelRequested: true }));
	}, [safeLog]);

	// Handler de retry
	const handleRetry = useCallback(() => {
		setState((prev) => ({
			...prev,
			status: "idle",
			error: null,
			progress: null,
			result: null,
		}));

		// Reset refs
		cancelRequestedRef.current = false;
		progressPrevRef.current = 0;
		processedMapRef.current = {};
		targetsRef.current = [];
		lastIndexConfirmedRef.current = 0;
	}, []);

	// Setters
	const setFormat = useCallback((format: ExportFormat) => {
		setState((prev) => ({ ...prev, format }));
		// carregar overrides persistidos do formato selecionado
		const loaded = loadOverridesForFormat(format);
		setOverrides(loaded);
	}, []);

	const setMode = useCallback((mode: ExportMode) => {
		setState((prev) => ({ ...prev, mode }));
	}, []);

	const setExportType = useCallback((type: ExportType) => {
		setState((prev) => ({ ...prev, exportType: type }));
	}, []);

	return {
		// Estado
		format: state.format,
		mode: state.mode,
		exportType: state.exportType,
		recommendedFormat: state.recommendedFormat,
		status: state.status,
		progress: state.progress,
		result: state.result,
		error: state.error,
		cancelRequested: state.cancelRequested,
		productionError: state.productionError,
		layoutError: state.layoutError,
		initError, // Expor erro de inicialização

		// Handlers
		setFormat,
		setMode,
		setExportType,
		handleExport,
		handleCancel,
		handleRetry,

		// Avançado
		showAdvanced,
		setShowAdvanced,
		useOverrides,
		setUseOverrides: (v: boolean) => {
			setUseOverrides(v);
			if (v) {
				const loaded = loadOverridesForFormat(state.format);
				setOverrides(loaded);
			}
		},
		overrides,
		setOverride: (key: string, value: unknown) => {
			setOverrides((prev) => {
				const next = { ...prev, [key]: value } as Record<string, unknown>;
				saveOverridesForFormat(state.format, next);
				return next;
			});
		},
	};
}
